/**
 * vendorPicker.tsx — T3 (wave „pola dodatkowe na listach i picker"), poprawka smoke
 * (owner: przycisk musi otwierać TĘ SAMĄ tabelę co pola odwołania — `RecordTablePicker`,
 * nie prostą listę wyszukiwania z `record-links`).
 *
 * Faktury zakupu/sprzedaży: pole `vendor_id` ma DWIE ścieżki wyboru kontrahenta
 * (owner decision, patrz brief T3):
 *  1. inline type-ahead — ISTNIEJĄCY `ContractorSearchPicker` (zostaje bez zmian),
 *  2. przycisk otwierający `RecordTablePicker` (`@/components/ui/RecordTablePicker`,
 *     entity='contractors', single-select) — DOKŁADNIE ten sam modal-tabela (katalog
 *     kolumn, wiersz filtra, popupy filtra nagłówka), którego używa renderer pola typu
 *     `reference` (`ExtFieldValueInput.tsx` → `ReferenceRecordInput`). Modal NIE ma
 *     opcji nadpisania tytułu (`RecordTablePickerCommonProps` go nie ma, a wnętrze
 *     komponentu jest poza zakresem tego pliku) — tytuł zostaje generyczny
 *     `ui.recordTablePicker.title`; tooltip/aria-label PRZYCISKU nadal używa dedykowanego
 *     `ui.vendorPicker.tablePickerTitle`.
 *
 * Obie ścieżki i OBIE klasy faktur (zakup/sprzedaż) muszą zapisywać
 * `vendorId`/`vendorNameCached`/`vendorTaxIdCached` IDENTYCZNIE — stąd JEDEN
 * wspólny helper `buildVendorAutoFillPatch` (czysta funkcja, zero side-effects)
 * używany przez oba renderery (`purchaseInvoiceCoreRenderers.tsx` /
 * `saleInvoiceCoreRenderers.tsx`) w obu ścieżkach. Regresja, której to pilnuje:
 * wybór przez modal z pustą nazwą (np. awaria dociągnięcia rekordu) nie może
 * cicho wyzerować cache'u nazwy w OBU miejscach dual-write (documents +
 * document_parties, patrz `vendor-autofill-dual-write.integration.test.ts`) —
 * `buildVendorAutoFillPatch` NIE wysyła puste/whitespace `vendor.name` (patrz
 * niżej), więc serwer spada na żywy rekord kontrahenta zamiast zapisać ''.
 * `vendor.taxId` przenoszony jest 1:1 (może być realnym `null` — kontrahent bez
 * NIP — to NIE jest błąd, w przeciwieństwie do pustej nazwy).
 *
 * `RecordTablePicker.onChange(id, row)` niesie PEŁNY wiersz w kształcie projekcji
 * `?columns=` (patrz jego JSDoc) — `row.taxId` jest OBECNY tylko gdy kolumna `tax_id`
 * jest częścią katalogu kartoteki z `showInList=true` (admin mógł ją ukryć z listy).
 * `VendorTablePickerButton.handleChange` czyta `row.name`/`row.taxId` BEZPOŚREDNIO z
 * wiersza gdy oba przejdą RUNTIME GUARD (nie samo rzutowanie typu — `row` jest
 * `[key: string]: unknown`) — `name` musi być `string`, `taxId` musi być `string`
 * ALBO `null` (kompletny brak NIP). Inaczej (kolumna NIP niewidoczna w katalogu →
 * klucz nieobecny, `name` nie jest stringiem, LUB `taxId` ma nieoczekiwany kształt,
 * np. `number` — dane uszkodzone) spada na DOTYCHCZASOWY fallback
 * `GET /contractors/:id` (ten sam endpoint, który `ContractorSearchPicker`/adapter
 * już zna). `RecordTablePicker` w trybie single-pick zamyka się SAM synchronicznie
 * (`handleRowClick`: `onChange(...)` → `onClose()`) — ewentualne dociąganie
 * fallbackiem zawsze biegnie PO zamknięciu. Przycisk pokazuje spinner (`resolving`)
 * w oknie między zamknięciem modala a zastosowaniem wyboru, a awaria fallbacku
 * (`catch`) informuje usera TOASTEM i NIE wywołuje `onPick` — pole zostaje bez
 * zmian, użytkownik wie czemu.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2, Table } from 'lucide-react';

import { ContractorFormModal } from '@/components/contractors/ContractorFormModal';
import { IconButton } from '@/components/IconButton';
import { RecordTablePicker, type RecordTablePickerRow } from '@/components/ui/RecordTablePicker';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

/** Kontrahent — kształt minimalny potrzebny do auto-fill (mirror `VendorRow` z ContractorSearchPicker). */
export interface VendorLike {
  name: string;
  taxId: string | null;
}

/**
 * Patch do `setMany` — pola identyczne w `PurchaseInvoiceFormData` i
 * `SaleInvoiceFormData` (`vendorId`/`vendorNameCached`/`vendorTaxIdCached`).
 * Gdy `vendor` jest `null` (ścieżka „wyczyść" inline pickera —
 * `ContractorSearchPicker` woła `onChange('', null)`), patch niesie WYŁĄCZNIE
 * `vendorId` — cache nazwy/NIP zostaje nietknięty (dosłowny parytet z
 * zachowaniem przed T3, żeby nie zmienić semantyki czyszczenia pola).
 */
export interface VendorAutoFillPatch {
  vendorId: string;
  vendorNameCached?: string | null;
  vendorTaxIdCached?: string | null;
}

export function buildVendorAutoFillPatch(
  id: string,
  vendor: VendorLike | null,
): VendorAutoFillPatch {
  if (!vendor) return { vendorId: id };
  // Puste/whitespace nazwa NIE jest wysyłana — kluczy `vendorNameCached` W OGÓLE
  // NIE MA w patchu (nie `vendorNameCached: undefined`). Powód: ten patch idzie
  // do `setMany`, który go SPREADuje w lokalny stan formularza (`{...form, ...patch}`)
  // — jawny `undefined` jako WARTOŚĆ klucza nadpisałby istniejące pole na `undefined`
  // (niezgodne z typem `string | null` i gubiące ewentualną poprzednią wartość w UI),
  // podczas gdy NIEOBECNOŚĆ klucza w spreadzie zostawia pole formularza nietknięte.
  // Serwer i tak spada na żywy rekord kontrahenta gdy pole nieobecne w body
  // (`data.vendorNameCached ?? vendor.name` w route.ts) — ten sam efekt end-to-end,
  // bez efektu ubocznego w lokalnym stanie po drodze.
  const patch: VendorAutoFillPatch = { vendorId: id, vendorTaxIdCached: vendor.taxId };
  if (vendor.name.trim()) patch.vendorNameCached = vendor.name;
  return patch;
}

interface ContractorDetailResponse {
  contractor: { id: string; name: string; taxId: string | null };
}

export interface VendorTablePickerButtonProps {
  /** Firma dla scopingu listy w modalu (Model B, CLAUDE.md §4.8). */
  companyId?: string | null;
  /**
   * Aktualnie wybrany kontrahent na dokumencie (vendor_id). Służy do podświetlenia
   * wiersza w oknie oraz do odświeżenia cache nazwy/NIP po edycji TEGO kontrahenta
   * z okna wyboru. `undefined`/`null` = brak wskazania.
   */
  value?: string | null;
  disabled?: boolean;
  /** Wołany PO dociągnięciu pełnego rekordu (id + {name, taxId}) — surowy, bez auto-fill. */
  onPick: (id: string, vendor: VendorLike) => void;
  'data-testid'?: string;
}

/**
 * Przycisk otwierający `RecordTablePicker` (entity='contractors', single-pick) —
 * druga ścieżka wyboru dostawcy/nabywcy na fakturze, obok inline type-ahead. TA SAMA
 * tabela co picker pola typu `reference` (katalog kolumn, filtr-wiersz, popupy filtra
 * nagłówka) — patrz nagłówek pliku. `handleChange` czyta `name`/`taxId` z wiersza gdy
 * oba obecne; inaczej fallback `GET /contractors/:id`. Awaria fallbacku → toast + pole
 * NIETKNIĘTE (patrz nagłówek pliku, [F]).
 */
export function VendorTablePickerButton({
  companyId,
  value,
  disabled,
  onPick,
  'data-testid': testId,
}: VendorTablePickerButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  // Okno formatki kontrahenta (dodaj/edytuj) osadzone NAD oknem wyboru — `null` = zamknięte.
  const [formState, setFormState] = useState<{
    mode: 'create' | 'edit';
    contractorId?: string;
  } | null>(null);

  /** Dociąga pełny rekord po id i woła `onPick` (create/edit z okna — brak wiersza w ręku). */
  async function resolveAndPick(id: string) {
    setResolving(true);
    try {
      const res = await api.get<ContractorDetailResponse>(`/contractors/${id}`);
      onPick(id, { name: res.data.contractor.name, taxId: res.data.contractor.taxId });
    } catch {
      toast.show({ variant: 'error', title: t('ui.vendorPicker.pickError') });
    } finally {
      setResolving(false);
    }
  }

  async function handleChange(id: string, row: RecordTablePickerRow) {
    setResolving(true);
    try {
      const rowName = typeof row.name === 'string' ? row.name : undefined;
      // Runtime guard (NIE rzutowanie typu) — `row` pochodzi z odpowiedzi API
      // (`[key: string]: unknown`), więc `taxId` może mieć dowolny kształt.
      // `null` jest realną, kompletną wartością (kontrahent bez NIP) — akceptowana.
      // `undefined` (klucz nieobecny — kolumna `tax_id` niewidoczna w katalogu
      // kartoteki, patrz nagłówek pliku) I każda INNA wartość (dane uszkodzone/
      // nieoczekiwane, np. `number`) skutkują `rowTaxId === undefined` → fallback.
      const rowTaxId = typeof row.taxId === 'string' || row.taxId === null ? row.taxId : undefined;
      if (rowName !== undefined && rowTaxId !== undefined) {
        onPick(id, { name: rowName, taxId: rowTaxId });
        return;
      }
      const res = await api.get<ContractorDetailResponse>(`/contractors/${id}`);
      onPick(id, { name: res.data.contractor.name, taxId: res.data.contractor.taxId });
    } catch {
      // Nie wywołujemy `onPick` — pole zostaje bez zmian (nie cichy zapis
      // niekompletnych danych), użytkownik dostaje wyraźny sygnał czemu wybór
      // z tabeli „nie zadziałał".
      toast.show({ variant: 'error', title: t('ui.vendorPicker.pickError') });
    } finally {
      setResolving(false);
    }
  }

  const pickerTestId = testId ? `${testId}-picker` : undefined;

  return (
    <>
      <IconButton
        variant="subtle"
        size="md"
        onClick={() => setOpen(true)}
        disabled={disabled || resolving}
        aria-label={t('ui.vendorPicker.tablePickerTitle')}
        title={t('ui.vendorPicker.tablePickerTitle')}
        data-testid={testId}
      >
        {resolving ? <Loader2 className="animate-spin" /> : <Table />}
      </IconButton>
      <RecordTablePicker
        entity="contractors"
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId ?? undefined}
        value={value ?? undefined}
        data-testid={pickerTestId}
        onChange={(id, row) => {
          void handleChange(id, row);
        }}
        recordActions={{
          // „Przejdź do kartoteki" — pełna kartoteka pod route'em (zamyka okno wyboru).
          onOpenCard: (row) => {
            setOpen(false);
            navigate(`/contractors/${row.id}`);
          },
          // „Edytuj" — formatka kontrahenta w modalu NAD oknem wyboru, bez wychodzenia.
          onEditRecord: (row) => setFormState({ mode: 'edit', contractorId: row.id }),
          // „Dodaj nowego" — formatka create w modalu, po zapisie wybiera nowy rekord.
          onCreateNew: () => setFormState({ mode: 'create' }),
          labels: {
            openCard: t('tenant.contractors.pickerActions.openCard'),
            editRecord: t('tenant.contractors.pickerActions.editSelected'),
            createNew: t('tenant.contractors.pickerActions.addNew'),
          },
        }}
      />
      <ContractorFormModal
        open={formState !== null}
        mode={formState?.mode ?? 'create'}
        contractorId={formState?.contractorId}
        companyId={companyId ?? undefined}
        onClose={() => setFormState(null)}
        onSaved={(id) => {
          const mode = formState?.mode;
          if (mode === 'create') {
            // Nowy kontrahent → zamknij formatkę i okno wyboru, wstaw na dokument.
            setFormState(null);
            setOpen(false);
            void resolveAndPick(id);
            return;
          }
          // Edit: jeśli to AKTUALNIE wybrany kontrahent — odśwież cache nazwy/NIP na
          // dokumencie. Formatkę zostawiamy otwartą (user może edytować dalej), zamknie ją X.
          if (value && id === value) void resolveAndPick(id);
        }}
        data-testid={testId ? `${testId}-form` : undefined}
      />
    </>
  );
}
